import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { addFoodEntry, deleteFoodEntry } from '../lib/api';
import { Check, ListChecks, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEAL_LABELS = {
    breakfast: { de: 'Frühstück', en: 'Breakfast' },
    lunch: { de: 'Mittagessen', en: 'Lunch' },
    dinner: { de: 'Abendessen', en: 'Dinner' },
    snack: { de: 'Snack', en: 'Snack' },
};

export function HabitMealChecklist({ date, onLogged }) {
    const { language } = useLanguage();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);

    const dateStr = date ? date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/nutrition/frequent-meals`, { params: { date: dateStr } });
            setData(res.data);
        } catch (e) {
            console.error('frequent-meals load failed:', e);
        }
    }, [dateStr]);

    useEffect(() => { load(); }, [load]);

    if (!data?.items?.length) return null;

    const handleToggle = async (item) => {
        const key = `${item.name}-${item.meal_type}`;
        setBusy(key);
        try {
            if (item.logged_today && item.today_entry_id) {
                await deleteFoodEntry(item.today_entry_id);
                toast.info(language === 'de' ? `${item.name} entfernt` : `${item.name} removed`);
            } else {
                await addFoodEntry({
                    name: item.name,
                    meal_type: item.meal_type,
                    calories: item.calories,
                    protein: item.protein,
                    carbs: item.carbs,
                    fat: item.fat,
                    fiber: item.fiber,
                    notes: language === 'de' ? 'Gewohnheits-Liste' : 'Habit list',
                    logged_at: new Date(`${dateStr}T${new Date().toISOString().split('T')[1]}`).toISOString(),
                });
                const talerNote = data.taler_enabled ? ` · ${item.taler} Taler` : '';
                toast.success(`${item.name} ✓ (${item.calories} kcal${talerNote})`);
            }
            await load();
            onLogged?.();
        } catch (e) {
            toast.error(language === 'de' ? 'Aktion fehlgeschlagen' : 'Action failed');
        } finally {
            setBusy(null);
        }
    };

    const grouped = {};
    for (const it of data.items) {
        (grouped[it.meal_type] = grouped[it.meal_type] || []).push(it);
    }
    const order = ['breakfast', 'lunch', 'dinner', 'snack'];

    return (
        <Card className="border" data-testid="habit-meal-checklist">
            <CardContent className="p-4">
                <p className="text-sm font-semibold mb-1 flex items-center gap-2">
                    <ListChecks className="w-4 h-4 icon-nutrition" />
                    {language === 'de' ? 'Deine üblichen Mahlzeiten' : 'Your usual meals'}
                </p>
                <p className="text-[11px] text-muted-foreground mb-3">
                    {language === 'de'
                        ? 'Einfach abhaken – ein Tipp loggt die Mahlzeit sofort.'
                        : 'Just tick it off – one tap logs the meal instantly.'}
                </p>
                <div className="space-y-3">
                    {order.filter(m => grouped[m]).map(meal => (
                        <div key={meal}>
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                                {MEAL_LABELS[meal]?.[language === 'de' ? 'de' : 'en'] || meal}
                            </p>
                            <div className="space-y-1">
                                {grouped[meal].map(item => {
                                    const key = `${item.name}-${item.meal_type}`;
                                    return (
                                        <button
                                            key={key}
                                            onClick={() => handleToggle(item)}
                                            disabled={busy === key}
                                            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border text-left transition-colors ${item.logged_today
                                                ? 'bg-green-50 border-green-300'
                                                : 'bg-background border-border hover:border-primary/40 hover:bg-primary/5'}`}
                                            data-testid={`habit-meal-item-${key.replace(/\s+/g, '-').toLowerCase()}`}
                                        >
                                            <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${item.logged_today
                                                ? 'bg-green-500 border-green-600 text-white'
                                                : 'border-muted-foreground/40'}`}>
                                                {busy === key
                                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                                                    : item.logged_today && <Check className="w-3.5 h-3.5" />}
                                            </span>
                                            <span className="flex-1 min-w-0">
                                                <span className={`text-sm truncate block ${item.logged_today ? 'line-through text-muted-foreground' : ''}`}>
                                                    {item.name}
                                                </span>
                                            </span>
                                            <span className="text-[11px] text-muted-foreground shrink-0">
                                                {item.calories} kcal
                                                {data.taler_enabled && <span className="text-amber-700 font-semibold"> · {item.taler} T</span>}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}
